import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingAPI } from '../api/onboarding';
import { TOUR_STEPS } from '../lib/tourSteps';
import {
  resolveAnchor, needsMobileMenu, waitForStableAnchor, popoverDescription, createAdvanceLock,
  createAnchorWatch, resumeIndex as computeResumeIndex,
} from '../lib/tourEngine';

// Orquesta el tour guiado (driver.js). La lógica pura —resolver anclas,
// esperar a que aparezcan, dónde reanudar— vive en lib/tourEngine.js.
//
// Reglas de producto (pedidas por Marco): la primera vez el tour no se puede
// cerrar (sin X, sin Esc, clic fuera no hace nada); se avanza con Siguiente
// hasta Finalizar. Si el usuario recarga, reanuda en el paso siguiente al
// último que la API registró (`current_step`).

const OnboardingCtx = createContext(null);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const waitUntil = async (cond, timeout = 3000, interval = 50) => {
  const started = Date.now();
  while (!cond() && Date.now() - started < timeout) await sleep(interval); // eslint-disable-line no-await-in-loop
  return cond();
};
// El drawer móvil es estado interno de Layout; se le habla por evento para
// no subir ese estado a un contexto global (#TourMobileAnchors).
const setMobileMenu = (open) => window.dispatchEvent(new CustomEvent('soem:tour-menu', { detail: { open } }));

export function OnboardingProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [onboarding, setOnboarding] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showQuickGuide, setShowQuickGuide] = useState(false);
  const driverRef = useRef(null);
  const lockRef = useRef(createAdvanceLock());
  const watchRef = useRef(null);
  const transitioningRef = useRef(false);
  // Ruta que React ya montó: el efecto corre después del commit. `navigate` cambia la URL al
  // instante, pero la página nueva (y el menú) se montan después; resaltar antes dejaba a
  // driver.js con nodos que iban a desaparecer (globo arriba a la izquierda, 27-oct).
  const committedPathRef = useRef(location.pathname);
  useEffect(() => { committedPathRef.current = location.pathname; }, [location.pathname]);

  const stopWatch = () => {
    if (watchRef.current) clearInterval(watchRef.current);
    watchRef.current = null;
  };

  // Si el proveedor se desmonta a medio tour (sesión vencida), el overlay de driver.js
  // vive en <body> y, sin X ni Esc, se quedaría encima del login.
  useEffect(() => () => { stopWatch(); driverRef.current?.destroy(); }, []);

  const steps = user ? (TOUR_STEPS[user.role] || []) : [];
  const resumeIndex = computeResumeIndex(onboarding, steps);
  const tourStatus = onboarding?.tour_completed ? 'completed' : resumeIndex > 0 ? 'in_progress' : 'never';

  const fetchOnboarding = useCallback(async () => {
    try {
      const r = await onboardingAPI.get();
      setOnboarding(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  // Arranque automático al iniciar sesión: respeta el interruptor global
  // (tour_enabled) y el modo (until_completed | always_show).
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    const timer = setTimeout(async () => {
      let tourConfig = { tour_enabled: true, tour_mode: 'until_completed' };
      try {
        const configRes = await onboardingAPI.getTourConfig();
        tourConfig = configRes.data || tourConfig;
      } catch {}
      if (!tourConfig.tour_enabled) return;
      const data = await fetchOnboarding();
      const shouldShow = tourConfig.tour_mode === 'always_show'
        || (data && !data.tour_completed && !data.tour_skipped);
      if (shouldShow) setShowWelcome(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.id]); // eslint-disable-line

  const startTour = useCallback(async (fromIndex = 0) => {
    if (typeof window === 'undefined' || steps.length === 0) return;
    const { driver } = await import('driver.js');
    await import('driver.js/dist/driver.css');

    setShowWelcome(false);
    setShowHelpMenu(false);
    setTourActive(true);
    setCurrentStepIndex(fromIndex);

    // Página en la que vive un paso: la del último `navigate` hasta ese índice.
    const pageOf = (idx) => {
      for (let i = idx; i >= 0; i -= 1) if (steps[i].navigate) return steps[i].navigate;
      return null;
    };

    // Lleva al usuario a la página del paso y espera a que React la monte; abre el drawer móvil
    // solo si el ancla vive ahí (y lo cierra en los demás pasos para que no tape la página);
    // luego espera a que el ancla esté quieta: mismo nodo, conectado, sin moverse.
    const prepare = async (idx) => {
      const step = steps[idx];
      if (!step) return;
      const page = pageOf(idx);
      if (page && committedPathRef.current !== page) {
        navigate(page);
        await waitUntil(() => committedPathRef.current === page);
      }
      setMobileMenu(needsMobileMenu(step.element));
      if (step.element) await waitForStableAnchor(step.element, { timeout: 3000 });
    };

    const driverSteps = steps.map((step, idx) => ({
      // Se resuelve al resaltar; sin ancla visible driver.js centra el globo.
      element: () => resolveAnchor(step.element) || undefined,
      popover: {
        title: `<span class="soem-tour-title">${step.title}</span>`,
        description: popoverDescription(step, idx, steps.length),
        side: step.side && step.side !== 'center' ? step.side : 'auto',
        align: 'start',
        nextBtnText: idx === steps.length - 1 ? 'Finalizar' : 'Siguiente',
        prevBtnText: 'Anterior',
        doneBtnText: 'Finalizar',
        showButtons: idx === 0 ? ['next'] : ['previous', 'next'],
      },
    }));

    const d = driver({
      popoverClass: 'soem-tour-popover',
      overlayColor: 'rgba(13, 27, 42, 0.80)',
      stagePadding: 8,
      stageRadius: 4,
      smoothScroll: true,
      animate: true,
      // Sin X ni Esc; el clic fuera del globo no hace nada (antes avanzaba
      // y quien quería "quitarlo" se saltaba pasos). Las flechas ← → sí
      // avanzan y retroceden (allowKeyboardControl por defecto).
      allowClose: false,
      overlayClickBehavior: () => {},
      steps: driverSteps,
      // driver.js mide el globo al dibujarlo, a media transición y antes de su tamaño final; en
      // celular creía que cabía a un lado y lo encimaba sobre el elemento. Se recalcula cuando la
      // transición termina: antes, su refresh() aún usa el elemento del paso anterior.
      onHighlightStarted: () => { transitioningRef.current = true; },
      onHighlighted: () => {
        requestAnimationFrame(() => { transitioningRef.current = false; d.refresh(); });
      },

      // El candado evita que un doble clic avance dos pasos mientras navega.
      onNextClick: () => lockRef.current.run(async () => {
        const idx = d.getActiveIndex() ?? 0;
        const stepDef = steps[idx];
        if (stepDef) {
          try { await onboardingAPI.step(stepDef.id, idx + 1); } catch {}
          setOnboarding((prev) => ({ ...(prev || {}), current_step: stepDef.id }));
        }
        if (idx >= steps.length - 1) {
          d.moveNext(); // sin paso siguiente driver.js destruye → onDestroyStarted
          return;
        }
        setCurrentStepIndex(idx + 1);
        await prepare(idx + 1);
        d.moveTo(idx + 1);
      }),

      onPrevClick: () => lockRef.current.run(async () => {
        const idx = d.getActiveIndex() ?? 0;
        const prevIdx = Math.max(0, idx - 1);
        setCurrentStepIndex(prevIdx);
        await prepare(prevIdx);
        d.moveTo(prevIdx);
      }),

      onDestroyStarted: async () => {
        stopWatch();
        const idx = d.getActiveIndex() ?? 0;
        if (idx >= steps.length - 1) {
          try { await onboardingAPI.complete(); } catch {}
          setOnboarding((prev) => ({ ...(prev || {}), tour_completed: true }));
          setShowCompletion(true);
        }
        setTourActive(false);
        d.destroy();
      },
    });

    driverRef.current = d;
    await prepare(fromIndex);
    d.drive(fromIndex);

    // driver.js solo recoloca con scroll o resize de la ventana; el vigilante cubre datos que
    // llegan tarde, scroll del menú y nodos que React reemplaza.
    stopWatch();
    watchRef.current = setInterval(createAnchorWatch({
      driver: d,
      resolveFor: (i) => resolveAnchor(steps[i]?.element),
      isBusy: () => lockRef.current.isBusy() || transitioningRef.current,
    }), 300);
  }, [steps, navigate]);

  // "Empezar desde el inicio" a medio tour: borra el avance en el servidor y arranca de cero.
  const resetAndRestart = useCallback(async () => {
    setShowHelpMenu(false);
    try { await onboardingAPI.reset(); } catch {}
    setOnboarding({ tour_completed: false, tour_skipped: false, current_step: null, steps_completed: [] });
    startTour(0);
  }, [startTour]);

  // "Ver el recorrido de nuevo" con el tour completado: no toca el registro del servidor,
  // así una recarga a medio recorrido no vuelve a forzar la bienvenida al siguiente login.
  const restartTour = useCallback(() => {
    setShowHelpMenu(false);
    startTour(0);
  }, [startTour]);

  const resumeTour = useCallback(() => {
    setShowHelpMenu(false);
    startTour(resumeIndex);
  }, [resumeIndex, startTour]);

  return (
    <OnboardingCtx.Provider value={{
      onboarding,
      showWelcome, setShowWelcome,
      showCompletion, setShowCompletion,
      tourActive,
      currentStepIndex,
      showHelpMenu, setShowHelpMenu,
      showQuickGuide, setShowQuickGuide,
      steps,
      resumeIndex,
      tourStatus,
      startTour,
      resetAndRestart,
      restartTour,
      resumeTour,
      fetchOnboarding,
    }}>
      {children}
    </OnboardingCtx.Provider>
  );
}

export const useOnboarding = () => useContext(OnboardingCtx);
